import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import Button from 'components/styleguide/button/Button';
import Hint from 'components/styleguide/hint/Hint';
import Icon from 'components/styleguide/icon/Icon';
import IconButton from 'components/styleguide/icon_button/IconButton';
import SectionEdition from 'components/styleguide/section_edition/SectionEdition';
import Slider from 'components/styleguide/slider/Slider';

const styles = require('./PhotoEdition.module.scss');

export type PhotoEditionContext = 'talent' | 'company' | 'neutral' | 'semantic';

// New file emits `{ file, crop: null }`, then `{ file, crop }` once the crop area is known.
export interface PhotoEditionChange {
  crop: Area | null;
  file: File | null;
}

export interface PhotoEditionProps {
  accept?: string;
  closable?: boolean;
  context?: PhotoEditionContext;
  defaultImageUrl?: string;
  error?: string;
  imageUrl?: string;
  maxSizeMb?: number;
  maxZoom?: number;
  minZoom?: number;
  onChange?: (change: PhotoEditionChange) => void;
  onClose?: () => void;
  onDelete?: () => void;
  onSave?: () => void;
  saveLabel?: string;
  title?: string;
  uploadHint?: string;
  zoomStep?: number;
}

const ALLOWED_TYPES = ['image/jpeg', 'image/png'];
const CROP_DIAMETER = 278;
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

function useDragDrop(onDrop: (e: React.DragEvent) => void) {
  const [isDragging, setIsDragging] = useState(false);
  const counter = useRef(0);

  return {
    isDragging,
    dragHandlers: {
      onDragOver: (e: React.DragEvent) => e.preventDefault(),
      onDragEnter: (e: React.DragEvent) => { e.preventDefault(); counter.current++; setIsDragging(true); },
      onDragLeave: (e: React.DragEvent) => { e.preventDefault(); counter.current--; if (counter.current === 0) setIsDragging(false); },
      onDrop: (e: React.DragEvent) => { counter.current = 0; setIsDragging(false); onDrop(e); },
    },
  };
}

const PhotoEdition: React.FC<PhotoEditionProps> = ({
  accept = 'image/jpeg,image/png',
  closable = false,
  context = 'talent',
  defaultImageUrl,
  error: externalError,
  imageUrl,
  maxSizeMb = 5,
  maxZoom = 3,
  minZoom = 1,
  onChange,
  onClose,
  onDelete,
  onSave,
  saveLabel = 'Save',
  title = 'Edit Photo',
  uploadHint = 'PNG or JPEG images only.',
  zoomStep = 0.1,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(defaultImageUrl ?? null);
  const [deleted, setDeleted] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [zoom, setZoom] = useState(minZoom);
  const [crop, setCrop] = useState({ x: 0, y: 0 });

  const objectUrlRef = useRef<string | null>(null);
  useEffect(() => () => { if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current); }, []);

  const displayUrl = previewUrl ?? (deleted ? null : imageUrl);
  const hasImage = !!displayUrl && !loadFailed;
  const displayError = internalError ?? externalError ?? null;

  // react-easy-crop has no error callback; probe the image and fall back to the empty state.
  useEffect(() => {
    setLoadFailed(false);
    if (!displayUrl) return undefined;
    let active = true;
    const probe = new Image();
    probe.onerror = () => { if (active) setLoadFailed(true); };
    probe.src = displayUrl;
    return () => { active = false; };
  }, [displayUrl]);

  const resetAdjustments = () => { setZoom(minZoom); setCrop({ x: 0, y: 0 }); };

  const acceptFile = useCallback((selected: File) => {
    if (!ALLOWED_TYPES.includes(selected.type)) {
      setInternalError('Unsupported format. Use a JPG or PNG image.');
      return;
    }
    if (selected.size > maxSizeMb * 1024 * 1024) {
      setInternalError(`File is too large. Maximum size is ${maxSizeMb} MB.`);
      return;
    }
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const url = URL.createObjectURL(selected);
    objectUrlRef.current = url;
    setInternalError(null);
    setLoadFailed(false);
    setDeleted(false);
    setFile(selected);
    setPreviewUrl(url);
    resetAdjustments();
    onChange?.({ file: selected, crop: null });
  }, [maxSizeMb, onChange]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) acceptFile(selected);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const selected = e.dataTransfer.files?.[0];
    if (selected) acceptFile(selected);
  };

  const openFilePicker = () => inputRef.current?.click();

  const handleCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    onChange?.({ file, crop: areaPixels });
  }, [file, onChange]);

  const handleDelete = () => {
    if (objectUrlRef.current) { URL.revokeObjectURL(objectUrlRef.current); objectUrlRef.current = null; }
    setFile(null);
    setPreviewUrl(null);
    setDeleted(true);
    setInternalError(null);
    resetAdjustments();
    onChange?.({ file: null, crop: null });
    onDelete?.();
  };

  const { isDragging, dragHandlers } = useDragDrop(handleDrop);
  const closeHandler = closable ? (onClose ?? (() => undefined)) : undefined;

  return (
    <SectionEdition
      className={styles['ui-photo-edition']}
      context={context}
      iconName="camera"
      title={title}
      onClose={closeHandler}
    >
      <div className={styles['ui-photo-edition__body']}>
        {hasImage ? (
          <>
            <div className={styles['ui-photo-edition__crop']}>
              <Cropper
                image={displayUrl as string}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropSize={{ width: CROP_DIAMETER, height: CROP_DIAMETER }}
                cropShape="round"
                showGrid={false}
                minZoom={minZoom}
                maxZoom={maxZoom}
                zoomSpeed={0.2}
                onCropChange={setCrop}
                onZoomChange={(z) => setZoom(clamp(z, minZoom, maxZoom))}
                onCropComplete={handleCropComplete}
              />
            </div>

            <div className={styles['ui-photo-edition__controls']}>
              <Slider
                steppers
                className={styles['ui-photo-edition__zoom']}
                aria-label="Zoom"
                aria-valuetext={`${Math.round(zoom * 100)}%`}
                stepDownLabel="Zoom out"
                stepUpLabel="Zoom in"
                context={context}
                min={minZoom}
                max={maxZoom}
                step={zoomStep}
                value={zoom}
                onChange={setZoom}
              />
              <IconButton
                type="terceary"
                context={context}
                size="M"
                iconName="repeat"
                aria-label="Replace image"
                onClick={openFilePicker}
              />
              <IconButton
                type="terceary"
                context={context}
                size="M"
                iconName="trash"
                aria-label="Delete image"
                onClick={handleDelete}
              />
            </div>
          </>
        ) : (
          <div
            className={classNames(
              styles['ui-photo-edition__dropzone'],
              { [styles['ui-photo-edition__dropzone--dragging']]: isDragging },
              { [styles['ui-photo-edition__dropzone--error']]: !!displayError },
            )}
            {...dragHandlers}
            role="button"
            tabIndex={0}
            aria-label={isDragging ? 'Drop image to upload' : 'Upload image'}
            onClick={openFilePicker}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFilePicker(); }
            }}
          >
            <div className={styles['ui-photo-edition__upload-icon']} aria-hidden="true">
              <Icon name="upload" size="md" color={`${context}-text-base`} />
            </div>
            <p className={styles['ui-photo-edition__upload-text']}>
              <span className={styles['ui-photo-edition__upload-link']}>Click to upload</span>
              {' '}or drop it here
            </p>
            <p className={styles['ui-photo-edition__upload-hint']}>{uploadHint}</p>
          </div>
        )}

        {displayError && <Hint variant="error" message={displayError} />}
      </div>

      <div className={styles['ui-photo-edition__footer']}>
        <Button context={context} type="primary" size="M" fullWidth onClick={onSave}>
          {saveLabel}
        </Button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className={styles['ui-photo-edition__input']}
        aria-label="Choose image to upload"
        tabIndex={-1}
      />
    </SectionEdition>
  );
};

export default PhotoEdition;
